import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete, canUserEdit } from '@/utils/permissions';
import {
  Package, Calendar, Search, Filter, RefreshCw, Upload, Download,
  FileSpreadsheet, TrendingUp, TrendingDown, BarChart3, Layers,
  ChevronDown, ChevronUp, AlertCircle, CheckCircle, X, Building2,
  Plus, Edit2, Trash2, Save, CalendarDays, ChevronLeft, ChevronRight
} from 'lucide-react';

// Dynamic month generator
function generateMonths() {
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const SHORT_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const months = [];
  let year = 2025, month = 10; // Start from Oct 2025
  const now = new Date();
  const curYear = now.getFullYear(), curMonth = now.getMonth() + 1;
  while (year < curYear || (year === curYear && month <= curMonth)) {
    months.push({
      key: `used${MONTH_NAMES[month-1]}${year}`,
      label: `${SHORT_NAMES[month-1]} ${year}`,
      short: SHORT_NAMES[month-1],
      year, month
    });
    month++;
    if (month > 12) { month = 1; year++; }
  }
  return months;
}

const MONTHS = generateMonths();

const RECORDS_PER_PAGE = 200;

const YEARS = [
  { value: 'all', label: 'All Years' },
  { value: '2025', label: '2025' },
  { value: '2026', label: '2026' },
];

const MONTH_OPTIONS = [
  { value: 'all', label: 'All Months' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const INITIAL_FORM_STATE = (() => {
  const state = {
    supplierName: '', receivingDate: '', invoiceNumber: '',
    materialType: '', receivingQuantity: '', balanceStock: ''
  };
  MONTHS.forEach(m => { state[m.key] = 0; });
  return state;
})();

export default function RawMaterialConsumptionStock() {
  const { API, user } = useContext(AppContext);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user can delete (Admin or 'full' access on this page)
  const canDelete = canUserDelete(user, '/raw-material/consumption-stock');
  const canEdit = canUserEdit(user, '/raw-material/consumption-stock');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMaterial, setFilterMaterial] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewMode, setViewMode] = useState('detail'); // 'detail' or 'summary'
  const [monthlyViewMode, setMonthlyViewMode] = useState('consumption'); // 'consumption' or 'receiving'
  
  // Time Period Filters
  const [filterYear, setFilterYear] = useState('all');
  const [filterMonthPeriod, setFilterMonthPeriod] = useState('all');
  const [timePeriodMode, setTimePeriodMode] = useState('all'); // 'all', 'yearly', 'monthly'
  const [currentPage, setCurrentPage] = useState(1);
  
  // Add Consignment Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({ suppliers: [], materialTypes: [] });
  const [editingRecord, setEditingRecord] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState([]);

  useEffect(() => {
    fetchRecords();
    fetchDropdownOptions();
    fetchMonthlySummary();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/master-data/raw-material/consumption_stock`);
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching records:', error);
    }
    setLoading(false);
  };

  const fetchMonthlySummary = async () => {
    try {
      const response = await axios.get(`${API}/raw-material/consumption-stock/monthly-summary`);
      setMonthlySummary(response.data.summaries || []);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const response = await axios.get(`${API}/master-data/raw-material/consumption-stock/dropdown-options`);
      setDropdownOptions(response.data);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate balance if receiving quantity changes
      if (field === 'receivingQuantity' || field.startsWith('used')) {
        const receivingQty = parseFloat(field === 'receivingQuantity' ? value : updated.receivingQuantity) || 0;
        const totalUsed = MONTHS.reduce((sum, m) => sum + (parseFloat(field === m.key ? value : updated[m.key]) || 0), 0);
        updated.balanceStock = (receivingQty - totalUsed).toFixed(2);
      }
      
      return updated;
    });
  };

  const handleAddConsignment = async () => {
    // Validation
    if (!formData.supplierName || !formData.receivingDate || !formData.invoiceNumber || !formData.materialType || !formData.receivingQuantity) {
      setMessage({ type: 'error', text: 'Please fill in all required fields (Supplier, Date, Invoice, Material Type, Quantity)' });
      setTimeout(() => setMessage(null), 4000);
      return;
    }

    setFormSubmitting(true);
    try {
      const payload = {
        ...formData,
        receivingQuantity: parseFloat(formData.receivingQuantity) || 0,
        balanceStock: formData.balanceStock ? parseFloat(formData.balanceStock) : null
      };
      MONTHS.forEach(m => {
        payload[m.key] = parseFloat(formData[m.key]) || 0;
      });

      if (editingRecord) {
        await axios.put(`${API}/master-data/raw-material/consumption-stock/${editingRecord.id}`, payload);
        setMessage({ type: 'success', text: 'Consignment updated successfully!' });
      } else {
        await axios.post(`${API}/master-data/raw-material/consumption-stock/create`, payload);
        setMessage({ type: 'success', text: 'Consignment added successfully!' });
      }
      
      setShowAddModal(false);
      setFormData(INITIAL_FORM_STATE);
      setEditingRecord(null);
      fetchRecords();
      fetchDropdownOptions();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save consignment' });
    }
    setFormSubmitting(false);
    setTimeout(() => setMessage(null), 4000);
  };

  const handleEditRecord = (record) => {
    const formBase = {
      supplierName: record.supplierName || '',
      receivingDate: record.receivingDate || '',
      invoiceNumber: record.invoiceNumber || '',
      materialType: record.materialType || '',
      receivingQuantity: record.receivingQuantity || '',
      balanceStock: record.balanceStock || ''
    };
    // Dynamically populate all month fields
    MONTHS.forEach(m => {
      formBase[m.key] = record[m.key] || 0;
    });
    setFormData(formBase);
    setEditingRecord(record);
    setShowAddModal(true);
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      await axios.delete(`${API}/master-data/raw-material/consumption-stock/${recordId}`);
      setMessage({ type: 'success', text: 'Consignment deleted successfully!' });
      setShowDeleteConfirm(null);
      fetchRecords();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete consignment' });
    }
    setTimeout(() => setMessage(null), 4000);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/master-data/import/consumption-stock`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        
      });
      setMessage({ type: 'success', text: `Successfully imported ${response.data.totalRecords} records! (Skipped ${response.data.skippedTotalRows} TOTAL rows)` });
      fetchRecords();
      setShowUploadModal(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to import data' });
    }
    setUploading(false);
    setTimeout(() => setMessage(null), 5000);
  };

  // Get unique values for filters
  const materialTypes = useMemo(() => {
    const types = new Set(records.map(r => r.materialType).filter(Boolean));
    return [...types].sort();
  }, [records]);

  const suppliers = useMemo(() => {
    const sups = new Set(records.map(r => r.supplierName).filter(Boolean));
    return [...sups].sort();
  }, [records]);

  // Filter records
  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      const matchesSearch = !searchTerm || 
        (r.supplierName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.invoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.materialType || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesMaterial = filterMaterial === 'all' || r.materialType === filterMaterial;
      const matchesSupplier = filterSupplier === 'all' || r.supplierName === filterSupplier;
      
      // Time period filtering based on receiving date
      let matchesTimePeriod = true;
      if (timePeriodMode !== 'all' && r.receivingDate) {
        const receivingDate = new Date(r.receivingDate);
        const receivingYear = receivingDate.getFullYear().toString();
        const receivingMonth = (receivingDate.getMonth() + 1).toString();
        
        if (timePeriodMode === 'yearly' && filterYear !== 'all') {
          matchesTimePeriod = receivingYear === filterYear;
        } else if (timePeriodMode === 'monthly') {
          if (filterYear !== 'all') {
            matchesTimePeriod = receivingYear === filterYear;
          }
          if (matchesTimePeriod && filterMonthPeriod !== 'all') {
            matchesTimePeriod = receivingMonth === filterMonthPeriod;
          }
        }
      }
      
      return matchesSearch && matchesMaterial && matchesSupplier && matchesTimePeriod;
    });
  }, [records, searchTerm, filterMaterial, filterSupplier, timePeriodMode, filterYear, filterMonthPeriod]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / RECORDS_PER_PAGE);
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
    return filteredRecords.slice(startIndex, startIndex + RECORDS_PER_PAGE);
  }, [filteredRecords, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterMaterial, filterSupplier, timePeriodMode, filterYear, filterMonthPeriod]);

  // Get filtered MONTHS based on time period selection
  const filteredMonths = useMemo(() => {
    if (timePeriodMode === 'all') return MONTHS;
    if (timePeriodMode === 'yearly' && filterYear !== 'all') {
      return MONTHS.filter(m => m.year.toString() === filterYear);
    }
    if (timePeriodMode === 'monthly' && filterYear !== 'all' && filterMonthPeriod !== 'all') {
      return MONTHS.filter(m => m.year.toString() === filterYear && m.month.toString() === filterMonthPeriod);
    }
    if (timePeriodMode === 'monthly' && filterYear !== 'all') {
      return MONTHS.filter(m => m.year.toString() === filterYear);
    }
    return MONTHS;
  }, [timePeriodMode, filterYear, filterMonthPeriod]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      totalReceived: 0,
      totalConsumed: 0,
      totalBalance: 0,
      byMaterial: {},
      monthlyConsumption: Object.fromEntries(MONTHS.map(m => [m.key, 0])),
      monthlyReceiving: Object.fromEntries(MONTHS.map(m => [m.key, 0])),
      totalReceipts: 0,
      uniqueSuppliers: new Set(),
      uniqueInvoices: new Set(),
    };

    filteredRecords.forEach(r => {
      const received = parseFloat(r.receivingQuantity) || 0;
      const balance = parseFloat(r.balanceStock) || 0;
      
      stats.totalReceived += received;
      stats.totalBalance += balance;
      stats.totalReceipts++;
      
      // Track unique suppliers and invoices
      if (r.supplierName) stats.uniqueSuppliers.add(r.supplierName);
      if (r.invoiceNumber) stats.uniqueInvoices.add(r.invoiceNumber);

      // Monthly consumption
      MONTHS.forEach(m => {
        const val = parseFloat(r[m.key]) || 0;
        stats.monthlyConsumption[m.key] += val;
        stats.totalConsumed += val;
      });

      // Monthly receiving - parse receivingDate to determine month
      if (r.receivingDate && received > 0) {
        try {
          const date = new Date(r.receivingDate);
          const monthIdx = date.getMonth(); // 0-11
          const year = date.getFullYear();
          const matchKey = MONTHS.find(m => m.year === year && m.month === (monthIdx + 1));
          if (matchKey) {
            stats.monthlyReceiving[matchKey.key] += received;
          }
        } catch (e) {
          // Ignore date parsing errors
        }
      }

      // By material type
      if (r.materialType) {
        if (!stats.byMaterial[r.materialType]) {
          stats.byMaterial[r.materialType] = { received: 0, consumed: 0, balance: 0 };
        }
        stats.byMaterial[r.materialType].received += received;
        stats.byMaterial[r.materialType].balance += balance;
        MONTHS.forEach(m => {
          stats.byMaterial[r.materialType].consumed += parseFloat(r[m.key]) || 0;
        });
      }
    });

    return stats;
  }, [filteredRecords]);

  // Material summary for summary view
  const materialSummary = useMemo(() => {
    return Object.entries(summaryStats.byMaterial).map(([material, data]) => ({
      material,
      ...data,
    })).sort((a, b) => b.received - a.received);
  }, [summaryStats]);

  const formatNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '-';
    // Convert KG to MT (1000 KG = 1 MT)
    const mtValue = parseFloat(num) / 1000;
    if (isNaN(mtValue)) return '-';
    return mtValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="rm-consumption-stock-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg">
            <Layers className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Raw Material Consumption & Stock</h1>
            <p className="text-sm text-gray-500">{records.length} records • Track receipts, consumption & balance</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={fetchRecords}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => {
              setFormData(INITIAL_FORM_STATE);
              setEditingRecord(null);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
            data-testid="add-consignment-btn"
          >
            <Plus size={18} />
            Add Consignment
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
            data-testid="import-data-btn"
          >
            <Upload size={18} />
            Import Excel
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Stats Cards - Receiving Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500 rounded-lg">
              <FileSpreadsheet className="text-white" size={20} />
            </div>
            <div>
              <p className="text-sm text-indigo-600 font-medium">Total Receipts</p>
              <p className="text-2xl font-bold text-indigo-900">{summaryStats.totalReceipts}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-4 border border-cyan-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500 rounded-lg">
              <Building2 className="text-white" size={20} />
            </div>
            <div>
              <p className="text-sm text-cyan-600 font-medium">Unique Suppliers</p>
              <p className="text-2xl font-bold text-cyan-900">{summaryStats.uniqueSuppliers?.size || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-4 border border-violet-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500 rounded-lg">
              <FileSpreadsheet className="text-white" size={20} />
            </div>
            <div>
              <p className="text-sm text-violet-600 font-medium">Unique Invoices</p>
              <p className="text-2xl font-bold text-violet-900">{summaryStats.uniqueInvoices?.size || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-4 border border-rose-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-500 rounded-lg">
              <Layers className="text-white" size={20} />
            </div>
            <div>
              <p className="text-sm text-rose-600 font-medium">Avg Qty/Receipt</p>
              <p className="text-2xl font-bold text-rose-900">
                {summaryStats.totalReceipts > 0 
                  ? formatNumber(summaryStats.totalReceived / summaryStats.totalReceipts) 
                  : '0.00'} <span className="text-sm font-normal">MT</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Stock Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Received</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summaryStats.totalReceived)} <span className="text-sm font-normal text-gray-500">MT</span></p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingDown className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Consumed</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summaryStats.totalConsumed)} <span className="text-sm font-normal text-gray-500">MT</span></p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Package className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Balance Stock</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summaryStats.totalBalance)} <span className="text-sm font-normal text-gray-500">MT</span></p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BarChart3 className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Material Types</p>
              <p className="text-2xl font-bold text-gray-900">{materialTypes.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Time Period Filter */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <CalendarDays size={18} className="text-indigo-600" />
            Filter by Time Period
          </h3>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Time Period Mode Toggle */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => { setTimePeriodMode('all'); setFilterYear('all'); setFilterMonthPeriod('all'); }}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  timePeriodMode === 'all' ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="time-all-tab"
              >
                All Time
              </button>
              <button
                onClick={() => { setTimePeriodMode('yearly'); setFilterMonthPeriod('all'); }}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  timePeriodMode === 'yearly' ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="time-yearly-tab"
              >
                Yearly
              </button>
              <button
                onClick={() => setTimePeriodMode('monthly')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  timePeriodMode === 'monthly' ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="time-monthly-tab"
              >
                Monthly
              </button>
            </div>
            
            {/* Year Dropdown - Show for Yearly and Monthly modes */}
            {(timePeriodMode === 'yearly' || timePeriodMode === 'monthly') && (
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm"
                data-testid="year-filter"
              >
                {YEARS.map(y => (
                  <option key={y.value} value={y.value}>{y.label}</option>
                ))}
              </select>
            )}
            
            {/* Month Dropdown - Show only for Monthly mode */}
            {timePeriodMode === 'monthly' && (
              <select
                value={filterMonthPeriod}
                onChange={(e) => setFilterMonthPeriod(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm"
                data-testid="month-filter"
              >
                {MONTH_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        
        {/* Active Filter Indicator */}
        {timePeriodMode !== 'all' && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-gray-500">Active filter:</span>
            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
              {timePeriodMode === 'yearly' && filterYear !== 'all' 
                ? `Year ${filterYear}` 
                : timePeriodMode === 'monthly' 
                  ? `${filterYear !== 'all' ? filterYear : 'All Years'}${filterMonthPeriod !== 'all' ? ` - ${MONTH_OPTIONS.find(m => m.value === filterMonthPeriod)?.label}` : ''}`
                  : 'All'}
            </span>
            <button
              onClick={() => { setTimePeriodMode('all'); setFilterYear('all'); setFilterMonthPeriod('all'); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Monthly Consumption / Receiving Bar with Tab Toggle */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Calendar size={18} className={monthlyViewMode === 'consumption' ? 'text-emerald-600' : 'text-blue-600'} />
            {monthlyViewMode === 'consumption' ? 'Monthly Consumption' : 'Monthly Receiving'}
            {filteredMonths.length < MONTHS.length 
              ? ` (${filteredMonths.map(m => m.short).join(', ')})`
              : ` (${MONTHS[0]?.label} - ${MONTHS[MONTHS.length - 1]?.label})`}
          </h3>
          {/* Tab Toggle */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMonthlyViewMode('consumption')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                monthlyViewMode === 'consumption' 
                  ? 'bg-white shadow text-emerald-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="monthly-consumption-tab"
            >
              <TrendingDown size={14} />
              Consumption
            </button>
            <button
              onClick={() => setMonthlyViewMode('receiving')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-1.5 ${
                monthlyViewMode === 'receiving' 
                  ? 'bg-white shadow text-blue-600' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="monthly-receiving-tab"
            >
              <TrendingUp size={14} />
              Receiving
            </button>
          </div>
        </div>
        
        {/* Monthly Cards - Consumption View */}
        {monthlyViewMode === 'consumption' && (
          <div className={`grid gap-3 ${filteredMonths.length <= 3 ? 'grid-cols-' + filteredMonths.length : 'grid-cols-6'}`} style={{gridTemplateColumns: `repeat(${Math.min(filteredMonths.length, 6)}, minmax(0, 1fr))`}}>
            {filteredMonths.map(month => (
              <div key={month.key} className="text-center">
                <div className="bg-gradient-to-b from-emerald-50 to-emerald-100 rounded-lg p-3 border border-emerald-200">
                  <p className="text-xs text-gray-500 mb-1">{month.label}</p>
                  <p className="text-lg font-bold text-emerald-700">{formatNumber(summaryStats.monthlyConsumption[month.key])}</p>
                  <p className="text-xs text-gray-400">MT</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Monthly Cards - Receiving View */}
        {monthlyViewMode === 'receiving' && (
          <div className={`grid gap-3`} style={{gridTemplateColumns: `repeat(${Math.min(filteredMonths.length, 6)}, minmax(0, 1fr))`}}>
            {filteredMonths.map(month => (
              <div key={month.key} className="text-center">
                <div className="bg-gradient-to-b from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                  <p className="text-xs text-gray-500 mb-1">{month.label}</p>
                  <p className="text-lg font-bold text-blue-700">{formatNumber(summaryStats.monthlyReceiving[month.key])}</p>
                  <p className="text-xs text-gray-400">MT</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search supplier, invoice, material..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-100 focus:border-emerald-400"
            />
          </div>
          <select
            value={filterMaterial}
            onChange={(e) => setFilterMaterial(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-100"
          >
            <option value="all">All Materials</option>
            {materialTypes.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-100 max-w-xs"
          >
            <option value="all">All Suppliers</option>
            {suppliers.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('detail')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'detail' ? 'bg-white shadow text-emerald-600' : 'text-gray-600'
              }`}
            >
              Detail View
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'summary' ? 'bg-white shadow text-emerald-600' : 'text-gray-600'
              }`}
            >
              Summary
            </button>
          </div>
        </div>

        {/* Material Type Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterMaterial('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              filterMaterial === 'all' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Materials
          </button>
          {materialTypes.map(m => (
            <button
              key={m}
              onClick={() => setFilterMaterial(m)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filterMaterial === m 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Last 6 Months Summary Cards */}
      {monthlySummary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="monthly-summary-cards">
          {monthlySummary.map((m, i) => {
            const isCurrentMonth = i === monthlySummary.length - 1;
            return (
              <div key={m.key} className={`rounded-xl border p-3 transition ${
                isCurrentMonth 
                  ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold ${isCurrentMonth ? 'text-emerald-700' : 'text-gray-500'}`}>
                    {m.label}
                  </span>
                  {isCurrentMonth && (
                    <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-bold">CURRENT</span>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Received</span>
                    <span className="font-semibold text-blue-600">{formatNumber(m.received)}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Consumed</span>
                    <span className="font-semibold text-red-500">{formatNumber(m.consumed)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-1 flex justify-between text-[11px]">
                    <span className="text-gray-500">Closing</span>
                    <span className={`font-bold ${m.closingBalance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatNumber(m.closingBalance)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary View */}
      {viewMode === 'summary' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">Material Type Summary</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Material Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total Received (MT)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total Consumed (MT)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Balance Stock (MT)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Utilization %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {materialSummary.map(item => (
                  <tr key={item.material} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{item.material}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatNumber(item.received)}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatNumber(item.consumed)}</td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">{formatNumber(item.balance)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.received > 0 && (item.consumed / item.received * 100) > 90 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.received > 0 ? ((item.consumed / item.received) * 100).toFixed(1) : 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-emerald-50 border-t-2 border-emerald-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-800">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{formatNumber(summaryStats.totalReceived)}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{formatNumber(summaryStats.totalConsumed)}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700">{formatNumber(summaryStats.totalBalance)}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700">
                    {summaryStats.totalReceived > 0 
                      ? ((summaryStats.totalConsumed / summaryStats.totalReceived) * 100).toFixed(1) 
                      : 0}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Detail View */}
      {viewMode === 'detail' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Receipt Details ({filteredRecords.length} records)</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-gray-400" />
              <p className="text-gray-500">Loading records...</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-8 text-center">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No records found</p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
              >
                Import Data
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Supplier</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Invoice</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Material</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600">Received</th>
                    {MONTHS.map(m => (
                      <th key={m.key} className="px-3 py-3 text-right text-xs font-semibold text-gray-600 bg-orange-50">{m.short}</th>
                    ))}
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 bg-emerald-50">Balance</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedRecords.map((r, idx) => (
                    <tr key={r.id || idx} className="hover:bg-gray-50 group">
                      <td className="px-3 py-2 text-gray-500">{(currentPage - 1) * RECORDS_PER_PAGE + idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900 max-w-[200px] truncate" title={r.supplierName}>
                        {r.supplierName || '-'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(r.receivingDate)}</td>
                      <td className="px-3 py-2 text-gray-600">{r.invoiceNumber || '-'}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                          {r.materialType || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">{formatNumber(r.receivingQuantity)}</td>
                      {MONTHS.map(m => (
                        <td key={m.key} className="px-3 py-2 text-right bg-orange-50/50">{formatNumber(r[m.key])}</td>
                      ))}
                      <td className="px-3 py-2 text-right bg-emerald-50/50 font-medium text-emerald-700">{formatNumber(r.balanceStock)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          {canEdit && (
                          <button
                            onClick={() => handleEditRecord(r)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Edit"
                            data-testid={`edit-record-${r.id}`}
                          >
                            <Edit2 size={14} />
                          </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setShowDeleteConfirm(r)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                              title="Delete"
                              data-testid={`delete-record-${r.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination Controls */}
              {filteredRecords.length > RECORDS_PER_PAGE && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * RECORDS_PER_PAGE + 1} - {Math.min(currentPage * RECORDS_PER_PAGE, filteredRecords.length)} of {filteredRecords.length} records
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-sm font-medium bg-emerald-100 text-emerald-700 rounded-lg">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Import Raw Material Data</h3>
              <button onClick={() => setShowUploadModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Upload your Excel file with the following columns:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 text-xs space-y-1">
                  <p><strong>Required columns:</strong></p>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>SUPPLIER NAME</li>
                    <li>RECEIVING DATE</li>
                    <li>INVOICE NUMBER</li>
                    <li>MATERIAL TYPE</li>
                    <li>RECEIVING QUANTITY</li>
                    <li>RAW MATERIAL USED IN OCTOBER 2025</li>
                    <li>RAW MATERIAL USED IN NOVEMBER 2025</li>
                    <li>RAW MATERIAL USED IN DECEMBER 2025</li>
                    <li>RAW MATERIAL USED IN JANUARY 2026</li>
                    <li>RAW MATERIAL USED IN FEBRUARY 2026</li>
                    <li>RAW MATERIAL USED IN MARCH 2026</li>
                    <li>RAW MATERIAL BALANCE STOCK</li>
                  </ul>
                </div>
              </div>
              
              <label className="block">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-emerald-400 transition cursor-pointer">
                  <FileSpreadsheet size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-2">Click to select Excel file</p>
                  <p className="text-xs text-gray-400">.xlsx or .xls files supported</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
              </label>
              
              {uploading && (
                <div className="mt-4 flex items-center justify-center gap-2 text-emerald-600">
                  <RefreshCw size={16} className="animate-spin" />
                  <span>Importing data...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Consignment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                {editingRecord ? <Edit2 size={18} /> : <Plus size={18} />}
                {editingRecord ? 'Edit Consignment' : 'Add New Consignment'}
              </h3>
              <button 
                onClick={() => { setShowAddModal(false); setEditingRecord(null); setFormData(INITIAL_FORM_STATE); }} 
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Package size={16} /> Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      list="suppliers-list"
                      value={formData.supplierName}
                      onChange={(e) => handleFormChange('supplierName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Select or type supplier name"
                      data-testid="supplier-name-input"
                    />
                    <datalist id="suppliers-list">
                      {dropdownOptions.suppliers.map(s => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Receiving Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.receivingDate}
                      onChange={(e) => handleFormChange('receivingDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="receiving-date-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Invoice Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.invoiceNumber}
                      onChange={(e) => handleFormChange('invoiceNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter invoice number"
                      data-testid="invoice-number-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Material Type <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      list="materials-list"
                      value={formData.materialType}
                      onChange={(e) => handleFormChange('materialType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Select or type material type"
                      data-testid="material-type-input"
                    />
                    <datalist id="materials-list">
                      {dropdownOptions.materialTypes.map(m => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Receiving Quantity (KG) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.receivingQuantity}
                      onChange={(e) => handleFormChange('receivingQuantity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter quantity in KG"
                      data-testid="receiving-qty-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Balance Stock (KG)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.balanceStock}
                      onChange={(e) => handleFormChange('balanceStock', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Auto-calculated"
                      data-testid="balance-stock-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated based on usage</p>
                  </div>
                </div>
              </div>
              
              {/* Monthly Usage */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Calendar size={16} /> Monthly Usage (KG)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {MONTHS.map(month => (
                    <div key={month.key}>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {month.label}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData[month.key]}
                        onChange={(e) => handleFormChange(month.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0"
                        data-testid={`${month.key}-input`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-gray-50">
              <button
                onClick={() => { setShowAddModal(false); setEditingRecord(null); setFormData(INITIAL_FORM_STATE); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddConsignment}
                disabled={formSubmitting}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition disabled:opacity-50"
                data-testid="save-consignment-btn"
              >
                {formSubmitting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editingRecord ? 'Update Consignment' : 'Add Consignment'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <Trash2 size={24} className="text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 text-lg">Delete Consignment</h3>
              </div>
              <p className="text-gray-600 mb-2">
                Are you sure you want to delete this consignment?
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                <p><strong>Supplier:</strong> {showDeleteConfirm.supplierName}</p>
                <p><strong>Invoice:</strong> {showDeleteConfirm.invoiceNumber}</p>
                <p><strong>Material:</strong> {showDeleteConfirm.materialType}</p>
              </div>
              <p className="text-red-600 text-sm">
                This action cannot be undone.
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteRecord(showDeleteConfirm.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
